import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useT } from '../lib/i18n'
import styles from './ResetPlaythrough.module.css'

/**
 * START OVER — end this playthrough and open a clean one.
 *
 * ⚠ THIS IS THE ONE DELIBERATE WALL-BREAK ON A PACK PAGE, and it is worth naming why it is
 * allowed. Doctrine is diegetic-only (`doctrine-world-is-paramount`): constraints emerge from the
 * world, and the product does not put buttons about itself in front of the fiction. But a reader
 * who cannot start over is not being protected from a wall-break — they are stuck, and *that* is
 * the louder break. So: allowed, and made as quiet as a control can be. It lives with the desk's
 * back-arrow, in the chrome the page already had, and never inside the fiction's own furniture.
 *
 * WHY IT LIVES ABOVE THE GRAMMARS rather than in each one. The desk carries `?session=` in the
 * address bar, so returning to it always resumes and there was no way back to a clean start
 * except editing the URL by hand. Messaging opens a fresh session on every mount, so today this
 * is nearly a no-op there — but the presence format's own design is *one eternal session, revived
 * per visit*, and on the day the surface honours that, this is the control that makes starting
 * over possible. Writing it once, above both, is what stops that day needing two.
 *
 * TWO-STEP, NO MODAL. A single click would be an accident waiting to happen on a surface whose
 * entire value is an accumulated relationship. A modal would be heavier than the act deserves.
 * So the word changes and waits, and forgets after a few seconds if nothing follows.
 */
export default function ResetPlaythrough({
  sessionId,
  onReset,
}: {
  /** The resumable session, when the grammar keeps one in the URL. Closed on reset. */
  sessionId: string | null
  onReset: () => void
}) {
  const t = useT()
  const [armed, setArmed] = useState(false)
  const [busy, setBusy] = useState(false)

  // Disarm on its own. An armed control left sitting is a trap for the next click, and the
  // reader who wandered off did not choose to stay one keystroke from wiping the thread.
  useEffect(() => {
    if (!armed) return
    const h = window.setTimeout(() => setArmed(false), 4000)
    return () => window.clearTimeout(h)
  }, [armed])

  async function click() {
    if (!armed) {
      setArmed(true)
      return
    }
    setBusy(true)
    // Erase the outgoing session first — everywhere it lives, not marked ended but gone — so
    // the control's own words ("forget everything… this cannot be undone") are true (2026-09-14).
    // Best-effort: a reset must not be blocked by a session the server has already forgotten.
    if (sessionId) {
      try {
        await api.forget(sessionId)
      } catch {
        /* already gone, or never persisted — either way, starting over is still correct */
      }
    }
    onReset()
    // This control sits OUTSIDE the remounted subtree — that is what lets it survive the reset
    // it triggers — so nothing else will clear these. Left set, the button stayed disabled and
    // still armed over a session that had already restarted: one click from ending the new one.
    setBusy(false)
    setArmed(false)
  }

  return (
    <button
      type="button"
      className={styles.reset}
      data-armed={armed || undefined}
      disabled={busy}
      onClick={() => void click()}
    >
      {armed ? t('reset.confirm') : t('reset.start')}
    </button>
  )
}
